import type { AiCreditPack, BillingInterval, BillingPlan } from 'definitions/billing-plans';
import { useState } from 'react';
import { toast } from 'sonner';
import { useClient } from 'hooks/useClient';
import { readApiError } from './api-error';

/**
 * `POST /api/billing/checkout-session` derives the customer, product and trial
 * length from the authenticated session (see checkout-session.ts). Redirects
 * the whole page to Polar's hosted checkout; there is no card form or PCI
 * surface in this app.
 *
 * The purchases are separate calls, not one. `startCheckout` buys an account's plan
 * (an agency buys one per client, with `accountId`) and carries the 12-day trial
 * for the payer's first; `startAgencyFee` buys the agency fee; `buyCreditPack` buys a
 * one-time credit pack and must not trial. They shared an entry point before, which
 * meant buying credits quietly started a subscription checkout.
 *
 * Every action here is for account Owners and Admins only (403 for a Member).
 * The screens hide them for Members; the messages below are the fallback.
 */
export type CheckoutPlan = {
  plan: BillingPlan;
  interval: BillingInterval;
  /** An agency's client the plan is for; the caller's own account when omitted. */
  accountId?: number | null;
};

type BuyCreditPackParams = { pack: AiCreditPack; accountId?: number | null };

const accountQuery = (accountId: number | null | undefined) => (accountId ? `&accountCompanyId=${accountId}` : '');

const NOT_AVAILABLE = 'Card payments are not available yet. Please try again later.';

export const useCheckout = () => {
  const apiClient = useClient();
  // Disables the buttons while a checkout is being created, so a slow or
  // failing request is not answered with more clicks.
  const [isStarting, setIsStarting] = useState(false);

  /**
   * The portal is a GET that returns a URL rather than redirecting, for the
   * same reason everything else here is fetched rather than linked: the API is
   * Bearer-only, so a plain `<a href="/api/billing/portal">` carries no
   * credential and 401s.
   */
  const openBillingPortal = async () => {
    try {
      const { url } = await apiClient.get<{ url: string }>('/billing/portal');
      window.location.href = url;
    } catch (error) {
      const { status, message } = readApiError(error);
      toast.error('Could not open the billing portal', {
        description:
          status === 503
            ? NOT_AVAILABLE
            : status === 404
              ? 'There is no billing account yet. Add a card first.'
              : status === 400 || status === 403
                ? (message ?? 'You do not have access to billing for this account.')
                : 'Please try again in a moment.',
      });
    }
  };

  /**
   * Whether the browser is on its way to Polar. False when it is not (checkout unavailable, refused, or an
   * existing subscription sent to the portal), so a caller that created something first can tell, since a
   * failure is toasted here and never thrown.
   */
  const redirectToCheckout = async (path: string): Promise<boolean> => {
    if (isStarting) return false;
    setIsStarting(true);
    try {
      const { url } = await apiClient.post<{ url: string }>(path);
      // Left disabled on success: the page is navigating away to Polar.
      window.location.href = url;
      return true;
    } catch (error) {
      setIsStarting(false);
      const { status, message } = readApiError(error);
      // One subscription per account and kind: a second checkout is refused, and the portal is
      // where the existing plan is managed.
      if (status === 409) {
        toast.info('This account already has a subscription. Opening the billing portal.');
        await openBillingPortal();
        return false;
      }
      toast.error('Could not start checkout', {
        description:
          status === 503
            ? NOT_AVAILABLE
            : status === 400 || status === 403
              ? (message ?? undefined)
              : 'Please try again in a moment.',
      });
      return false;
    }
  };

  return {
    /** The plan the trial (or, for a returning account, the subscription) starts on. */
    startCheckout: ({ plan, interval, accountId }: CheckoutPlan) =>
      redirectToCheckout(`/billing/checkout-session?plan=${plan}&interval=${interval}${accountQuery(accountId)}`),
    /** The agency fee, monthly or annual. The agency's own, with its own trial. */
    startAgencyFee: (interval: BillingInterval) =>
      redirectToCheckout(`/billing/checkout-session?kind=agency_fee&interval=${interval}`),
    buyCreditPack: ({ pack, accountId }: BuyCreditPackParams) =>
      redirectToCheckout(`/billing/checkout-session?kind=credit_pack&pack=${pack}${accountQuery(accountId)}`),
    openBillingPortal,
    isStarting,
  };
};
